import { Modal } from '../common/Modal';
import { IFreelancerSearchResponse } from '../../interfaces/freelancerSearch';
import { useBullhornLookup } from '../../queryHooks/bullhorn/useBullhornLookup';
import { ICustomerFullProfile } from '../../interfaces/customer';
import { Loader } from '../common/Loader';
import { ColumnBoxFlexFullWidth, FlexBox, FlexBoxFullWidth } from '../common/boxes';
import { TEXTS } from '../../core/texts';
import { useNotification } from '../../+state/useNotification';
import { useCallback } from 'react';
import { ReadOnlyTextField } from '../common/ReadOnlyTextField';
import { Box, IconButton, Tooltip } from '@mui/material';
import { ContentCopy, OpenInNew } from '@mui/icons-material';
import { getTruncatedName } from '../../utils/getTruncatedName';
import { parseCamelCaseToLabel } from '../../utils/parseCamelCaseToLabel';
import { BorderBoxWithTitle } from '../common/BorderBoxWithTitle';

interface IProps {
  onClose: () => void;
  freelancer: IFreelancerSearchResponse | ICustomerFullProfile;
}

const BullhornProfileModal = ({ onClose, freelancer }: IProps) => {
  const { openInfoToast } = useNotification();

  const copyToClipboardHandler = useCallback((val: string | number) => {
    navigator.clipboard.writeText(val.toString());
    openInfoToast(TEXTS.COPIED_TO_CLIPBOARD, 1000);
  }, [openInfoToast]);

  const { data, isFetching } = useBullhornLookup({ customerId: freelancer.id, email: freelancer.email });
  const openBullhornLinkInNewTab = useCallback(() => data && window.open(data.bullhornLink), [data]);

  return (
    <Modal onClose={onClose} maxWidth={800} keepMounted title={TEXTS.BULLHORN_CANDIDATE_PROFILE}>
      {
        isFetching && <Loader width={{ mobile: 50, tablet: 80 }}/>
      }
      {
        !!data &&
        <ColumnBoxFlexFullWidth>
          <FlexBoxFullWidth flexWrap={'wrap'}>
            <FlexBox mr={{ mobile: 0, tablet: 2 }}>
              <FlexBox alignItems={'center'} mr={2}>
                <ReadOnlyTextField label={'Bullhorn ID'} value={data.bullhornId}/>
                <Box>
                  <Tooltip title={TEXTS.COPY}>
                    <IconButton onClick={() => copyToClipboardHandler(data.bullhornId)}>
                      <ContentCopy/>
                    </IconButton>
                  </Tooltip>
                </Box>
              </FlexBox>
              <FlexBox alignItems={'center'}>
                <ReadOnlyTextField label={'Customer ID'} value={data.customerId}/>
                <Box>
                  <Tooltip title={TEXTS.COPY}>
                    <IconButton onClick={() => copyToClipboardHandler(data.customerId)}>
                      <ContentCopy/>
                    </IconButton>
                  </Tooltip>
                </Box>
              </FlexBox>
            </FlexBox>
            <FlexBox alignItems={'center'} width={{mobile: '100%', tablet: 'fit-content'}}>
              <ReadOnlyTextField label={'Bullhorn link'} value={getTruncatedName(data.bullhornLink, 30)}/>
              <Box>
                <Tooltip title={TEXTS.OPEN_IN_NEW_TAB}>
                  <IconButton onClick={openBullhornLinkInNewTab}>
                    <OpenInNew/>
                  </IconButton>
                </Tooltip>
              </Box>
            </FlexBox>
          </FlexBoxFullWidth>

          {
            Object.entries(data).map(([key, value]) => {
              if (['bullhornLink', 'bullhornId', 'customerId'].includes(key)) { return null;}
              if(Array.isArray(value)) {
                return (
                  <ReadOnlyTextField key={key} label={parseCamelCaseToLabel(key)} value={value.join(', ')} multiline/>
                )
              }
              if (typeof value === 'object') {
                return (<BorderBoxWithTitle title={parseCamelCaseToLabel(key)} key={key}>
                  {
                    Object.entries(value as { [key: string]: string | number }).map(([nestKey, nestVal]) =>
                      (
                        <ReadOnlyTextField key={key + nestKey} multiline label={parseCamelCaseToLabel(nestKey)} value={nestVal}/>
                      ))
                  }
                </BorderBoxWithTitle>);
              }
              return (
                <ReadOnlyTextField key={key} label={parseCamelCaseToLabel(key)} value={value} multiline/>
              );
            })
          }
        </ColumnBoxFlexFullWidth>
      }
    </Modal>
  );
};

export default BullhornProfileModal;